import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, Alert, ActivityIndicator, Platform, AppState, AppStateStatus, TouchableOpacity, ScrollView } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import AppSwitch from '@/components/AppSwitch';
// OneSignal will be imported dynamically to prevent startup crashes
import Toast from '@/components/Toast';
import AsyncStorage from '@react-native-async-storage/async-storage';
import Constants from 'expo-constants';
import PageHeader from '@/components/PageHeader';
import { Skeleton } from '@/components/Skeleton';
import { env } from '@/config/environment';
import { Svg, Path } from 'react-native-svg';
import oneSignalService from '@/services/oneSignalService';

interface NotificationPermissionState {
  hasNotificationPermission: boolean;
  pushDisabled: boolean;
  subscribed: boolean;
}

interface OneSignalDebugStatus {
  initialized: boolean;
  moduleLoaded: boolean;
  currentUserId: string | null;
  environment: {
    isWeb: boolean;
    isExpoGo: boolean;
    isSupported: boolean;
    executionEnvironment: string;
  };
  pushPermission: string | null;
  subscriptionId: string | null;
  optedIn: boolean | null;
  pushToken: string | null;
  onesignalId: string | null;
  lastImportError?: string | null;
  lastInitError?: string | null;
}

interface OneSignalProbe {
  importOk: boolean;
  importError: string | null;
  importErrorName: string | null;
  moduleKeyCount: number;
  hasInitializeMethod: boolean;
  hasUserMethod: boolean;
  hasNotificationsMethod: boolean;
  initializedAfter: boolean;
  moduleLoadedAfter: boolean;
  initError: string | null;
  subscriptionId: string | null;
  pushToken: string | null;
  optedIn: boolean | null;
  appVersion: string | null;
  nativeAppVersion: string | null;
  nativeBuildVersion: string | null;
  runtimeVersion: string | null;
  updateId: string | null;
  channel: string | null;
  executionEnvironment: string;
  platform: string;
}

interface ServerVerifyResponse {
  success: boolean;
  found: boolean;
  authError?: boolean;
  userId: string;
  message: string;
  data?: {
    onesignalId: string;
    externalId: string;
    subscriptionCount: number;
    pushSubscriptions: Array<{
      id: string;
      type: string;
      enabled: boolean;
      token: string;
    }>;
    hasActivePushSubscription: boolean;
  };
}

const NOTIFICATION_SETTINGS_KEY = '@padrinho_notification_settings';
// Improved Expo Go detection
const isExpoGo = Constants.executionEnvironment === 'storeClient' || 
                Constants.appOwnership === 'expo' ||
                (Constants.expoConfig?.slug === 'padrinho-elo' && !Constants.expoConfig?.extra?.eas);

function NotificationSettingsScreen() {
  const router = useRouter();
  const { user, userProfile, session } = useAuth();
  const isAdmin = userProfile?.is_admin === true;
  const { t } = useLanguage();
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [initializing, setInitializing] = useState(true);
  const [toastVisible, setToastVisible] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState<'success' | 'error' | 'info'>('success');
  const [permissionState, setPermissionState] = useState<NotificationPermissionState | null>(null);

  // Admin OneSignal debug state
  const [showDebug, setShowDebug] = useState(false);
  const [debugStatus, setDebugStatus] = useState<OneSignalDebugStatus | null>(null);
  const [probeResult, setProbeResult] = useState<OneSignalProbe | null>(null);
  const [serverStatus, setServerStatus] = useState<ServerVerifyResponse | null>(null);
  const [debugLoading, setDebugLoading] = useState(false);
  const [testPushLoading, setTestPushLoading] = useState(false);
  const [reRegisterLoading, setReRegisterLoading] = useState(false);

  useEffect(() => {
    // Non-admins never reach the settings UI (route is admin-only), so skip all
    // OneSignal/AsyncStorage side effects for them. Re-runs once the profile
    // resolves and isAdmin flips true.
    if (!isAdmin) {
      setInitializing(false);
      return;
    }

    initializeNotificationSettings();

    // BUGFIX: Add AppState listener to detect when user returns from iOS Settings
    // This is essential for fixing the OneSignal permission detection bug
    const handleAppStateChange = (nextAppState: AppStateStatus) => {
      if (nextAppState === 'active') {
        console.log('📱 App became active - re-checking OneSignal permissions after Settings visit');
        
        // Add delay to allow OneSignal SDK to sync with iOS Settings changes
        setTimeout(() => {
          checkCurrentPermissionState();
        }, 1500); // 1.5 second delay as recommended by OneSignal docs
      }
    };

    const appStateSubscription = AppState.addEventListener('change', handleAppStateChange);

    // Cleanup function
    return () => {
      appStateSubscription?.remove();
    };
  }, [isAdmin]);

  const initializeNotificationSettings = async () => {
    try {
      setInitializing(true);

      // Load user preferences
      await loadNotificationSettings();

      // Check if OneSignal is supported (web not supported by React Native SDK)
      if (Platform.OS === 'web') {
        console.log('📱 Web environment detected - OneSignal React Native SDK not applicable');
        return;
      }

      // Check if running in Expo Go
      if (isExpoGo) {
        console.log('ℹ️ Notification Settings: OneSignal not supported in Expo Go');
        return;
      }

      // Check if OneSignal App ID is configured
      if (!env.oneSignalAppId || env.oneSignalAppId.trim() === '') {
        console.log('📱 OneSignal App ID not configured - skipping initialization');
        return;
      }

      console.log('🔍 Notification Settings: OneSignal supported in current environment');

      // Link the user via the singleton service (single source of truth).
      // FIRE-AND-FORGET: never await this in the screen-load path. setExternalUserId()
      // ensures the SDK is initialized, and initialize() can retry for tens of
      // seconds (or never settle) when the native module is missing — awaiting it
      // here froze the screen on the skeleton loader. We refresh the toggle once
      // the link resolves so it still reflects the real state.
      if (user?.id) {
        console.log('🔗 Notification Settings: Linking user via OneSignal service...');
        oneSignalService
          .setExternalUserId(user.id)
          .then(() => {
            console.log('✅ OneSignal user linked with ID:', user.id);
            checkCurrentPermissionState();
          })
          .catch((error: any) => console.warn('⚠️ Failed to link user:', error?.message));
      }

      // Check current permission state (reads cached state only — see below)
      await checkCurrentPermissionState();

    } catch (error: any) {
      console.error('❌ Notification Settings: Initialization error:', error);

      let errorMessage = t('notifications.errorLoading');

      if (error.message?.includes('OneSignal')) {
        errorMessage = t('notifications.serviceUnavailable');
      } else if (error.message?.includes('environment')) {
        errorMessage = t('notifications.unsupportedEnvironment');
      } else if (error.message?.includes('initialization')) {
        errorMessage = t('notifications.initializationFailed');
      }

      showToast(errorMessage, 'error');
    } finally {
      setInitializing(false);
    }
  };

  const loadNotificationSettings = async () => {
    try {
      const stored = await AsyncStorage.getItem(NOTIFICATION_SETTINGS_KEY);
      if (stored) {
        const settings = JSON.parse(stored);
        setNotificationsEnabled(settings.enabled || false);
        console.log('📱 Notification Settings: Loaded user preference:', settings.enabled);
      }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error('❌ Notification Settings: Error loading settings:', errorMessage);
    }
  };

  const checkCurrentPermissionState = async () => {
    try {
      // Single source of truth: read state from the singleton service. Its
      // getDebugStatus() uses the async v5 getters (getPermissionAsync /
      // getOptedInAsync / getIdAsync) — which avoid the stale cached value the
      // synchronous getter returns on cold start.
      // IMPORTANT: do NOT pass { ensureInitialized: true } here. This runs in the
      // screen-load path, and forcing initialize() can retry for tens of seconds
      // (or hang when the native module is missing), freezing the skeleton. The
      // SDK is already initialized at app startup (_layout.tsx), and the fire-and-
      // forget link above re-calls this once init settles. The admin debug panel
      // still force-inits via loadDebugStatus(), which has its own spinner.
      const status = await oneSignalService.getDebugStatus();

      const hasPermission = status.pushPermission === 'granted';
      const subscribed = !!(status.optedIn && status.subscriptionId);

      const permissionStatus: NotificationPermissionState = {
        hasNotificationPermission: hasPermission,
        pushDisabled: !hasPermission,
        subscribed: subscribed && hasPermission
      };

      setPermissionState(permissionStatus);
      console.log('📱 Notification Settings: Current permission state:', permissionStatus);

      // The toggle reflects the real OS permission state.
      setNotificationsEnabled(hasPermission);
    } catch (error: any) {
      console.error('❌ Notification Settings: Error checking permission state:', error?.message);
    }
  };

  const saveNotificationSettings = async (enabled: boolean) => {
    try {
      const settings = { 
        enabled, 
        updatedAt: new Date().toISOString(),
        userId: user?.id 
      };
      await AsyncStorage.setItem(NOTIFICATION_SETTINGS_KEY, JSON.stringify(settings));
      console.log('💾 Notification Settings: Saved user preference:', enabled);
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error('❌ Notification Settings: Error saving settings:', errorMessage);
    }
  };

  const showToast = (message: string, type: 'success' | 'error' | 'info' = 'success') => {
    setToastMessage(message);
    setToastType(type);
    setToastVisible(true);
  };

  const handleToggleNotifications = async (enabled: boolean) => {
    // PRD §5.11 — `notification_settings_changed`. Fired at handler entry
    // so we capture the user's intent regardless of permission outcome
    // (the system permission prompt may deny the request, but the toggle
    // intent is what matters for the dashboard).
    import('@/utils/clarityEvents')
      .then(({ logNotificationSettingsChanged }) =>
        logNotificationSettingsChanged({ category: 'all', enabled })
      )
      .catch(() => {});

    if (loading) return;

    try {
      setLoading(true);

      if (enabled) {
        console.log('🔔 Notification Settings: Requesting permission...');

        // Check environment support first
        if (Platform.OS === 'web') {
          throw new Error(t('notifications.webUnsupported'));
        }
        
        if (isExpoGo) {
          throw new Error(t('notifications.expoGoUnsupported'));
        }

        if (!env.oneSignalAppId || env.oneSignalAppId.trim() === '') {
          throw new Error(t('notifications.oneSignalNotConfigured'));
        }

        console.log('✅ Notification Settings: OneSignal is ready, requesting permission...');

        // Request permission through the singleton service. It ensures the SDK
        // is initialized and returns false (not throws) when unsupported, so a
        // failed import no longer surfaces a misleading "service unavailable".
        const granted = await oneSignalService.requestPermission();

        if (granted) {
          console.log('✅ Notification Settings: Permission granted');

          // Link the user for targeted notifications via the service.
          if (user?.id) {
            try {
              console.log('🔧 Notification Settings: Linking user...');
              await oneSignalService.setExternalUserId(user.id);
              console.log('✅ Notification Settings: User linked for notifications');
            } catch (userIdError: any) {
              console.warn('⚠️ Notification Settings: Failed to link user:', userIdError?.message);
            }
          }

          setNotificationsEnabled(true);
          await saveNotificationSettings(true);
          showToast(t('notifications.enabledSuccess'), 'success');

          // Check permission state after successful setup
          setTimeout(async () => {
            console.log('🔄 Checking permission status after setup...');
            await checkCurrentPermissionState();
          }, 1500);
        } else {
          console.log('❌ Notification Settings: Permission denied');
          setNotificationsEnabled(false);
          showToast(t('notifications.permissionDenied'), 'error');
        }
      } else {
        console.log('ℹ️ Notification Settings: Notifications disabled by user preference');
        setNotificationsEnabled(false);
        await saveNotificationSettings(false);

        Alert.alert(
          t('notifications.disabled'),
          t('notifications.disabledAlertMessage'),
          [{ text: t('notifications.understood'), style: 'default' }]
        );
      }
    } catch (error: any) {
      console.error('❌ Notification Settings: Error toggling notifications:', error);

      // More specific error messages
      let errorMessage = t('notifications.errorLoading');

      if (error.message?.includes('not supported')) {
        errorMessage = t('notifications.unsupportedEnvironment');
      } else if (error.message?.includes('initialize') || error.message?.includes('initialization')) {
        errorMessage = t('notifications.initializationFailed');
      } else if (error.message?.includes('OneSignal')) {
        errorMessage = t('notifications.serviceUnavailable');
      }

      showToast(errorMessage, 'error');

      // Revert toggle state on error
      await checkCurrentPermissionState();
    } finally {
      setLoading(false);
    }
  };

  // Admin OneSignal debug functions
  const loadDebugStatus = async () => {
    if (!isAdmin || !session?.user?.id) return;

    setDebugLoading(true);
    try {
      // Single source of truth: the singleton service. ensureInitialized makes
      // the panel stop reporting "not initialized" when the SDK can actually
      // start, and probe() captures the EXACT import/init error + build/OTA ids.
      const [clientStatus, probe] = await Promise.all([
        oneSignalService.getDebugStatus({ ensureInitialized: true }),
        oneSignalService.probe(),
      ]);
      setDebugStatus(clientStatus);
      setProbeResult(probe);

      // Load server-side verification (admin-only endpoint — send JWT)
      const serverUrl = `${env.apiUrl}/api/onesignal/verify-user?userId=${session.user.id}`;
      const headers: Record<string, string> = {};
      if (session?.access_token) {
        headers['Authorization'] = `Bearer ${session.access_token}`;
      }
      const response = await fetch(serverUrl, { headers });
      const serverData = await response.json();
      setServerStatus(serverData);
    } catch (error) {
      console.error('Failed to load debug status:', error);
      Alert.alert('Error', 'Failed to load OneSignal debug status');
    } finally {
      setDebugLoading(false);
    }
  };

  const handleTestPush = async () => {
    if (!isAdmin || !session?.access_token || !session?.user?.id) return;

    setTestPushLoading(true);
    try {
      const response = await fetch(`${env.apiUrl}/api/notifications/test-push`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          userId: session.user.id,
          title: '🧪 Test Push',
          message: 'This is a test push notification from the debug panel'
        })
      });

      const result = await response.json();

      if (result.success) {
        Alert.alert('Success', `Push sent! Notification ID: ${result.notificationId?.substring(0, 8)}...`);
      } else {
        Alert.alert('Failed', result.error || 'Failed to send test push');
      }
    } catch (error: any) {
      Alert.alert('Error', error?.message || 'Failed to send test push');
    } finally {
      setTestPushLoading(false);
    }
  };

  const handleForceReRegister = async () => {
    if (!isAdmin || !session?.user?.id) return;

    Alert.alert(
      'Force Re-Register',
      'This will logout and re-login with OneSignal to fix broken subscriptions. Continue?',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Re-Register',
          onPress: async () => {
            setReRegisterLoading(true);
            try {
              const success = await oneSignalService.forceReRegister(session.user.id);

              if (success) {
                Alert.alert('Success', 'OneSignal re-registration successful! Refreshing status...');
                await loadDebugStatus();
              } else {
                Alert.alert('Failed', 'Re-registration failed. Check logs for details.');
              }
            } catch (error: any) {
              Alert.alert('Error', error?.message || 'Re-registration failed');
            } finally {
              setReRegisterLoading(false);
            }
          }
        }
      ]
    );
  };

  const handleClose = () => {
    router.back();
  };

  const renderEnvironmentInfo = () => {
    // Check environment directly without OneSignalService
    if (isExpoGo) {
      return (
        <View style={styles.warningCard}>
          <Text style={styles.warningTitle}>{t('notifications.expoGoWarningTitle')}</Text>
          <Text style={styles.warningText}>
            {t('notifications.expoGoWarningMessage')}
          </Text>
        </View>
      );
    }

    if (Platform.OS === 'web') {
      return (
        <View style={styles.warningCard}>
          <Text style={styles.warningTitle}>{t('notifications.webWarningTitle')}</Text>
          <Text style={styles.warningText}>
            {t('notifications.webWarningMessage')}
          </Text>
        </View>
      );
    }

    if (!env.oneSignalAppId || env.oneSignalAppId.trim() === '') {
      return (
        <View style={styles.warningCard}>
          <Text style={styles.warningTitle}>{t('notifications.oneSignalWarningTitle')}</Text>
          <Text style={styles.warningText}>
            {t('notifications.oneSignalWarningMessage')}
          </Text>
        </View>
      );
    }

    return null;
  };

  if (initializing) {
    return (
      <SafeAreaView style={styles.container} edges={['top']}>
        <PageHeader 
          title={t('notifications.title')} 
          onBack={handleClose}
          backgroundColor="#FFFFFF"
        />

        <View style={styles.skeletonContent}>
          {/* Toggle row skeleton */}
          <View style={styles.skeletonSection}>
            <View style={styles.skeletonToggleRow}>
              <View style={styles.skeletonToggleInfo}>
                <Skeleton width={160} height={16} borderRadius={4} />
                <Skeleton width="90%" height={14} borderRadius={4} style={{ marginTop: 8 }} />
                <Skeleton width="70%" height={14} borderRadius={4} style={{ marginTop: 4 }} />
              </View>
              <Skeleton width={51} height={31} borderRadius={16} />
            </View>
          </View>

          {/* Info card skeleton */}
          <View style={styles.skeletonInfoCard}>
            <Skeleton width={40} height={40} borderRadius={20} />
            <View style={{ flex: 1, gap: 8 }}>
              <Skeleton width={140} height={14} borderRadius={4} />
              <Skeleton width="100%" height={12} borderRadius={4} />
              <Skeleton width="80%" height={12} borderRadius={4} />
            </View>
          </View>
        </View>
      </SafeAreaView>
    );
  }

  if (!isAdmin) {
    return (
      <SafeAreaView style={styles.container} edges={['top']}>
        <PageHeader 
          title={t('notifications.title')} 
          onBack={handleClose}
          backgroundColor="#FFFFFF"
        />
        <View style={styles.unauthorizedContainer}>
          <Text style={styles.unauthorizedText}>{t('notifications.adminOnly')}</Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container} edges={['top']}>
      <PageHeader 
        title={t('notifications.title')} 
        onBack={handleClose}
        backgroundColor="#FFFFFF"
      />

      <ScrollView
        style={styles.content}
        contentContainerStyle={styles.contentContainer}
        showsVerticalScrollIndicator={false}
      >
        {/* Environment Warning */}
        {renderEnvironmentInfo()}

        {/* Main Toggle Section */}
        <View style={styles.section}>
          <View style={styles.toggleContainer}>
            <View style={styles.toggleInfo}>
              <Text style={styles.toggleTitle}>{t('notifications.pushNotifications')}</Text>
              <Text style={styles.toggleDescription}>
                {t('notifications.pushDescription')}
              </Text>
              {permissionState && (
                <Text style={styles.permissionStatus}>
                  Status: {permissionState.hasNotificationPermission ? t('notifications.statusAllowed') : t('notifications.statusDenied')} • 
                  {permissionState.subscribed ? t('notifications.statusSubscribed') : t('notifications.statusNotSubscribed')}
                </Text>
              )}
              {__DEV__ && permissionState && (
                <Text style={styles.debugStatus}>
                  Debug: permission={permissionState.hasNotificationPermission ? 'true' : 'false'}, 
                  pushDisabled={permissionState.pushDisabled ? 'true' : 'false'}, 
                  subscribed={permissionState.subscribed ? 'true' : 'false'}
                </Text>
              )}
            </View>
            <View style={styles.switchContainer}>
              {loading && <ActivityIndicator size="small" color="#7E7D75" style={styles.switchLoader} />}
              <AppSwitch
                value={notificationsEnabled}
                onValueChange={handleToggleNotifications}
                disabled={loading || isExpoGo || Platform.OS === 'web'}
              />
            </View>
          </View>
        </View>

        {/* Information Section */}
        <View style={styles.infoSection}>
          <View style={styles.infoCard}>
            <View style={styles.infoIcon}>
              <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <Path d="M12 2V6" stroke="#7E7D75" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <Path d="M12 18V22" stroke="#7E7D75" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <Path d="M22 12H18" stroke="#7E7D75" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <Path d="M6 12H2" stroke="#7E7D75" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <Path d="M4.92871 4.92892L7.75714 7.75735" stroke="#7E7D75" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <Path d="M16.2427 16.2426L19.0711 19.0711" stroke="#7E7D75" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <Path d="M19.0713 4.92892L16.2429 7.75735" stroke="#7E7D75" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <Path d="M7.75732 16.2426L4.9289 19.0711" stroke="#7E7D75" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </Svg>
            </View>
            <View style={styles.infoContent}>
              <Text style={styles.infoTitle}>{t('notifications.whatYouWillReceive')}</Text>
              <Text style={styles.infoText}>
                {t('notifications.receiveList')}
              </Text>
            </View>
          </View>
        </View>

        {/* Privacy Note */}
        <View style={styles.privacySection}>
          <Text style={styles.privacyTitle}>{t('notifications.privacyTitle')}</Text>
          <Text style={styles.privacyText}>
            {t('notifications.privacyNote')}
          </Text>
        </View>

        {/* Admin OneSignal Debug Panel (moved here from the notifications list) */}
        <View style={styles.osDebugSection}>
          <TouchableOpacity
            style={styles.osDebugToggle}
            onPress={() => {
              setShowDebug(!showDebug);
              if (!showDebug && !debugStatus) {
                loadDebugStatus();
              }
            }}
          >
            <Text style={styles.osDebugToggleText}>
              🔧 OneSignal Debug {showDebug ? '▼' : '▶'}
            </Text>
          </TouchableOpacity>

          {showDebug && (
            <ScrollView style={styles.osDebugContent} nestedScrollEnabled={true}>
              {debugLoading ? (
                <ActivityIndicator size="small" color="#151515" />
              ) : (
                <>
                  {/* Client Status */}
                  <Text style={styles.osDebugSubtitle}>Client Status</Text>
                  <View style={styles.osDebugRow}>
                    <Text style={styles.osDebugLabel}>Initialized:</Text>
                    <Text style={[styles.osDebugValue, debugStatus?.initialized ? styles.osDebugSuccess : styles.osDebugError]}>
                      {debugStatus?.initialized ? '✅ Yes' : '❌ No'}
                    </Text>
                  </View>
                  <View style={styles.osDebugRow}>
                    <Text style={styles.osDebugLabel}>Module Loaded:</Text>
                    <Text style={[styles.osDebugValue, debugStatus?.moduleLoaded ? styles.osDebugSuccess : styles.osDebugError]}>
                      {debugStatus?.moduleLoaded ? '✅ Yes' : '❌ No'}
                    </Text>
                  </View>
                  <View style={styles.osDebugRow}>
                    <Text style={styles.osDebugLabel}>Environment:</Text>
                    <Text style={styles.osDebugValue}>
                      {debugStatus?.environment?.executionEnvironment || 'unknown'}
                    </Text>
                  </View>
                  <View style={styles.osDebugRow}>
                    <Text style={styles.osDebugLabel}>Supported:</Text>
                    <Text style={[styles.osDebugValue, debugStatus?.environment?.isSupported ? styles.osDebugSuccess : styles.osDebugError]}>
                      {debugStatus?.environment?.isSupported ? '✅ Yes' : '❌ No'}
                    </Text>
                  </View>
                  <View style={styles.osDebugRow}>
                    <Text style={styles.osDebugLabel}>Push Permission:</Text>
                    <Text style={[styles.osDebugValue, debugStatus?.pushPermission === 'granted' ? styles.osDebugSuccess : styles.osDebugError]}>
                      {debugStatus?.pushPermission || 'unknown'}
                    </Text>
                  </View>
                  <View style={styles.osDebugRow}>
                    <Text style={styles.osDebugLabel}>Opted In:</Text>
                    <Text style={[styles.osDebugValue, debugStatus?.optedIn ? styles.osDebugSuccess : styles.osDebugError]}>
                      {debugStatus?.optedIn === true ? '✅ Yes' : debugStatus?.optedIn === false ? '❌ No' : 'unknown'}
                    </Text>
                  </View>
                  <View style={styles.osDebugRow}>
                    <Text style={styles.osDebugLabel}>Push Token:</Text>
                    <Text style={[styles.osDebugValue, debugStatus?.pushToken ? styles.osDebugSuccess : styles.osDebugError]}>
                      {debugStatus?.pushToken || 'none'}
                    </Text>
                  </View>
                  <View style={styles.osDebugRow}>
                    <Text style={styles.osDebugLabel}>Current User ID:</Text>
                    <Text style={styles.osDebugValue} numberOfLines={1}>
                      {debugStatus?.currentUserId?.substring(0, 16) || 'none'}...
                    </Text>
                  </View>
                  {!!debugStatus?.lastInitError && (
                    <View style={styles.osDebugRow}>
                      <Text style={styles.osDebugLabel}>Last Init Error:</Text>
                      <Text style={[styles.osDebugValue, styles.osDebugError]}>
                        {debugStatus.lastInitError}
                      </Text>
                    </View>
                  )}

                  {/* SDK Probe — the decisive signal. A native-module import
                      failure here means the running binary lacks the OneSignal
                      native module and needs an EAS rebuild (an OTA can't fix
                      it). If import succeeds but the SDK still won't init, it's
                      a JS-side problem that an OTA can address. */}
                  <Text style={[styles.osDebugSubtitle, { marginTop: 16 }]}>SDK Probe</Text>
                  <View style={styles.osDebugRow}>
                    <Text style={styles.osDebugLabel}>Native import:</Text>
                    <Text style={[styles.osDebugValue, probeResult?.importOk ? styles.osDebugSuccess : styles.osDebugError]}>
                      {probeResult ? (probeResult.importOk ? '✅ OK' : '❌ FAILED') : '—'}
                    </Text>
                  </View>
                  {!!probeResult?.importError && (
                    <View style={styles.osDebugRow}>
                      <Text style={styles.osDebugLabel}>Import error:</Text>
                      <Text style={[styles.osDebugValue, styles.osDebugError]}>
                        {probeResult.importErrorName ? `${probeResult.importErrorName}: ` : ''}{probeResult.importError}
                      </Text>
                    </View>
                  )}
                  {probeResult?.importOk && (
                    <>
                      <View style={styles.osDebugRow}>
                        <Text style={styles.osDebugLabel}>Module keys:</Text>
                        <Text style={styles.osDebugValue}>{probeResult.moduleKeyCount}</Text>
                      </View>
                      <View style={styles.osDebugRow}>
                        <Text style={styles.osDebugLabel}>initialize()/User/Notifications:</Text>
                        <Text style={styles.osDebugValue}>
                          {probeResult.hasInitializeMethod ? '✅' : '❌'}/{probeResult.hasUserMethod ? '✅' : '❌'}/{probeResult.hasNotificationsMethod ? '✅' : '❌'}
                        </Text>
                      </View>
                    </>
                  )}
                  <View style={styles.osDebugRow}>
                    <Text style={styles.osDebugLabel}>Init after probe:</Text>
                    <Text style={[styles.osDebugValue, probeResult?.initializedAfter ? styles.osDebugSuccess : styles.osDebugError]}>
                      {probeResult ? (probeResult.initializedAfter ? '✅ Yes' : '❌ No') : '—'}
                    </Text>
                  </View>
                  {!!probeResult?.initError && (
                    <View style={styles.osDebugRow}>
                      <Text style={styles.osDebugLabel}>Init error:</Text>
                      <Text style={[styles.osDebugValue, styles.osDebugError]}>
                        {probeResult.initError}
                      </Text>
                    </View>
                  )}
                  <View style={styles.osDebugRow}>
                    <Text style={styles.osDebugLabel}>App / native build:</Text>
                    <Text style={styles.osDebugValue} numberOfLines={1}>
                      {probeResult?.appVersion || '?'} / {probeResult?.nativeBuildVersion || '?'}
                    </Text>
                  </View>
                  <View style={styles.osDebugRow}>
                    <Text style={styles.osDebugLabel}>Runtime / channel:</Text>
                    <Text style={styles.osDebugValue} numberOfLines={1}>
                      {probeResult?.runtimeVersion || '?'} / {probeResult?.channel || '?'}
                    </Text>
                  </View>
                  <View style={styles.osDebugRow}>
                    <Text style={styles.osDebugLabel}>Update ID:</Text>
                    <Text style={styles.osDebugValue} numberOfLines={1}>
                      {probeResult?.updateId ? probeResult.updateId.substring(0, 12) + '…' : 'embedded'}
                    </Text>
                  </View>

                  {/* Server Status */}
                  <Text style={[styles.osDebugSubtitle, { marginTop: 16 }]}>Server Verification</Text>
                  {serverStatus?.authError ? (
                    <View style={styles.osDebugRow}>
                      <Text style={styles.osDebugLabel}>Server → OneSignal:</Text>
                      <Text style={[styles.osDebugValue, styles.osDebugError]}>
                        ⚠️ Auth failed — fix ONESIGNAL_REST_API_KEY
                      </Text>
                    </View>
                  ) : (
                    <View style={styles.osDebugRow}>
                      <Text style={styles.osDebugLabel}>Found in OneSignal:</Text>
                      <Text style={[styles.osDebugValue, serverStatus?.found ? styles.osDebugSuccess : styles.osDebugError]}>
                        {serverStatus?.found ? '✅ Yes' : '❌ No'}
                      </Text>
                    </View>
                  )}
                  {serverStatus?.data && (
                    <>
                      <View style={styles.osDebugRow}>
                        <Text style={styles.osDebugLabel}>Subscriptions:</Text>
                        <Text style={styles.osDebugValue}>
                          {serverStatus.data.subscriptionCount}
                        </Text>
                      </View>
                      <View style={styles.osDebugRow}>
                        <Text style={styles.osDebugLabel}>Active Push:</Text>
                        <Text style={[styles.osDebugValue, serverStatus.data.hasActivePushSubscription ? styles.osDebugSuccess : styles.osDebugError]}>
                          {serverStatus.data.hasActivePushSubscription ? '✅ Yes' : '❌ No'}
                        </Text>
                      </View>
                      {serverStatus.data.pushSubscriptions?.map((sub, idx) => (
                        <View key={idx} style={styles.osDebugRow}>
                          <Text style={styles.osDebugLabel}>{sub.type}:</Text>
                          <Text style={[styles.osDebugValue, sub.enabled ? styles.osDebugSuccess : styles.osDebugError]}>
                            {sub.enabled ? '✅ enabled' : '❌ disabled'} | token: {sub.token}
                          </Text>
                        </View>
                      ))}
                    </>
                  )}
                  <Text style={styles.osDebugMessage}>{serverStatus?.message}</Text>

                  {/* Actions */}
                  <View style={styles.osDebugActions}>
                    <TouchableOpacity
                      style={styles.osDebugButton}
                      onPress={loadDebugStatus}
                      disabled={debugLoading}
                    >
                      <Text style={styles.osDebugButtonText}>🔄 Refresh</Text>
                    </TouchableOpacity>

                    <TouchableOpacity
                      style={[styles.osDebugButton, styles.osDebugButtonPrimary]}
                      onPress={handleTestPush}
                      disabled={testPushLoading}
                    >
                      {testPushLoading ? (
                        <ActivityIndicator size="small" color="#FFFFFF" />
                      ) : (
                        <Text style={[styles.osDebugButtonText, styles.osDebugButtonTextPrimary]}>📤 Test Push</Text>
                      )}
                    </TouchableOpacity>

                    <TouchableOpacity
                      style={[styles.osDebugButton, styles.osDebugButtonDanger]}
                      onPress={handleForceReRegister}
                      disabled={reRegisterLoading}
                    >
                      {reRegisterLoading ? (
                        <ActivityIndicator size="small" color="#FFFFFF" />
                      ) : (
                        <Text style={[styles.osDebugButtonText, styles.osDebugButtonTextPrimary]}>🔄 Re-Register</Text>
                      )}
                    </TouchableOpacity>
                  </View>
                </>
              )}
            </ScrollView>
          )}
        </View>

        {/* Technical Info (Debug) */}
        {__DEV__ && (
          <View style={styles.debugSection}>
            <Text style={styles.debugTitle}>Debug Info</Text>
            <Text style={styles.debugText}>
              Environment: {Constants.executionEnvironment}{'\n'}
              Platform: {Platform.OS}{'\n'}
              Expo Go: {isExpoGo ? 'Yes' : 'No'}{'\n'}
              OneSignal App ID: {env.oneSignalAppId ? 'Configured' : 'Missing'}{'\n'}
              Supported: {(!isExpoGo && Platform.OS !== 'web' && !!env.oneSignalAppId) ? 'Yes' : 'No'}{'\n'}
              User ID: {user?.id ? 'Set' : 'None'}
            </Text>
          </View>
        )}
      </ScrollView>

      {/* Toast Feedback */}
      <Toast
        visible={toastVisible}
        type={toastType}
        message={toastMessage}
        onHide={() => setToastVisible(false)}
        duration={4000}
      />
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#FFFFFF',
  },
  unauthorizedContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 32,
  },
  unauthorizedText: {
    fontSize: 14,
    fontFamily: 'Inter',
    color: '#7E7D75',
    textAlign: 'center',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 24,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#F0EEE4',
    backgroundColor: '#FFFFFF',
  },
  backButton: {
    padding: 4,
  },
  headerTitle: {
    fontSize: 14,
    fontFamily: 'Inter',
    fontWeight: 'bold',
    color: '#151515',
  },
  placeholder: {
    width: 24,
  },
  content: {
    flex: 1,
    paddingHorizontal: 24,
  },
  warningCard: {
    marginVertical: 16,
    padding: 16,
    backgroundColor: '#FFF3CD',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#FFEAA7',
  },
  warningTitle: {
    fontSize: 14,
    fontFamily: 'Inter_600SemiBold',
    color: '#856404',
    marginBottom: 8,
  },
  warningText: {
    fontSize: 14,
    fontFamily: 'Inter_400Regular',
    color: '#856404',
    lineHeight: 20,
  },
  section: {
    paddingVertical: 24,
    borderBottomWidth: 1,
    borderBottomColor: '#F0EEE4',
  },
  toggleContainer: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 16,
  },
  toggleInfo: {
    flex: 1,
  },
  toggleTitle: {
    fontSize: 16,
    fontFamily: 'Inter_600SemiBold',
    color: '#151515',
    marginBottom: 4,
  },
  toggleDescription: {
    fontSize: 14,
    fontFamily: 'Inter_400Regular',
    color: '#757C85',
    lineHeight: 20,
    marginBottom: 8,
  },
  permissionStatus: {
    fontSize: 12,
    fontFamily: 'Inter_400Regular',
    color: '#757C85',
    fontStyle: 'italic',
  },
  switchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  switchLoader: {
    marginRight: 8,
  },
  infoSection: {
    paddingVertical: 24,
    borderBottomWidth: 1,
    borderBottomColor: '#F0EEE4',
  },
  infoCard: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 16,
    padding: 16,
    backgroundColor: '#F9F9F7',
    borderRadius: 12,
  },
  infoIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#FFFFFF',
    justifyContent: 'center',
    alignItems: 'center',
  },
  infoContent: {
    flex: 1,
  },
  infoTitle: {
    fontSize: 14,
    fontFamily: 'Inter_600SemiBold',
    color: '#151515',
    marginBottom: 8,
  },
  infoText: {
    fontSize: 14,
    fontFamily: 'Inter_400Regular',
    color: '#757C85',
    lineHeight: 20,
  },
  privacySection: {
    paddingVertical: 24,
  },
  privacyTitle: {
    fontSize: 14,
    fontFamily: 'Inter_600SemiBold',
    color: '#151515',
    marginBottom: 8,
  },
  privacyText: {
    fontSize: 14,
    fontFamily: 'Inter_400Regular',
    color: '#757C85',
    lineHeight: 20,
  },
  debugSection: {
    marginTop: 24,
    padding: 16,
    backgroundColor: '#F8F9FA',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E9ECEF',
  },
  debugTitle: {
    fontSize: 12,
    fontFamily: 'Inter_600SemiBold',
    color: '#6C757D',
    marginBottom: 8,
  },
  debugText: {
    fontSize: 11,
    fontFamily: 'Inter_400Regular',
    color: '#6C757D',
    lineHeight: 16,
  },
  debugStatus: {
    fontSize: 10,
    fontFamily: 'Inter_400Regular',
    color: '#6C757D',
    fontStyle: 'italic',
    marginTop: 4,
  },
  skeletonContent: {
    flex: 1,
    paddingHorizontal: 24,
  },
  skeletonSection: {
    paddingVertical: 24,
    borderBottomWidth: 1,
    borderBottomColor: '#F0EEE4',
  },
  skeletonToggleRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 16,
  },
  skeletonToggleInfo: {
    flex: 1,
  },
  skeletonInfoCard: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 16,
    padding: 16,
    backgroundColor: '#F9F9F7',
    borderRadius: 12,
    marginTop: 24,
  },
  contentContainer: {
    paddingBottom: 40,
  },
  // Admin OneSignal debug panel styles
  osDebugSection: {
    marginTop: 24,
    backgroundColor: '#F5F5F0',
    borderRadius: 12,
    overflow: 'hidden',
  },
  osDebugToggle: {
    padding: 16,
    backgroundColor: '#E8E8E0',
  },
  osDebugToggleText: {
    fontSize: 14,
    fontFamily: 'Inter',
    fontWeight: '600',
    color: '#151515',
  },
  osDebugContent: {
    padding: 16,
    maxHeight: 350,
  },
  osDebugSubtitle: {
    fontSize: 12,
    fontFamily: 'Inter',
    fontWeight: '700',
    color: '#7E7D75',
    textTransform: 'uppercase',
    marginBottom: 8,
  },
  osDebugRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 4,
    borderBottomWidth: 1,
    borderBottomColor: '#E8E8E0',
  },
  osDebugLabel: {
    fontSize: 12,
    fontFamily: 'Inter',
    color: '#7E7D75',
    flex: 1,
  },
  osDebugValue: {
    fontSize: 12,
    fontFamily: 'Inter',
    fontWeight: '500',
    color: '#151515',
    flex: 2,
    textAlign: 'right',
  },
  osDebugSuccess: {
    color: '#22C55E',
  },
  osDebugError: {
    color: '#EF4444',
  },
  osDebugMessage: {
    fontSize: 11,
    fontFamily: 'Inter',
    color: '#7E7D75',
    fontStyle: 'italic',
    marginTop: 8,
    textAlign: 'center',
  },
  osDebugActions: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    marginTop: 16,
    gap: 8,
  },
  osDebugButton: {
    flex: 1,
    paddingVertical: 10,
    paddingHorizontal: 12,
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E8E8E0',
    alignItems: 'center',
  },
  osDebugButtonPrimary: {
    backgroundColor: '#151515',
    borderColor: '#151515',
  },
  osDebugButtonDanger: {
    backgroundColor: '#EF4444',
    borderColor: '#EF4444',
  },
  osDebugButtonText: {
    fontSize: 12,
    fontFamily: 'Inter',
    fontWeight: '600',
    color: '#151515',
  },
  osDebugButtonTextPrimary: {
    color: '#FFFFFF',
  },
});

export default NotificationSettingsScreen;